import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth } from "../firebase";
import {
  isAndroidNativeApp,
  isValidRouteCallId,
  logAndroidWebRouteBlocked,
  logAndroidWebMountPrevented,
  logAndroidNativeRedirectFailed,
  logBrowserNativeRouteBlocked,
} from "./callRoute";
import {
  resolveReturnContext,
  removeReturnContext,
  markNativeCallReturnStarted,
  mapCallReturnToastMessage,
  CALL_RETURN_FALLBACK_URL,
} from "./nativeCallReturnContext";

const REDIRECT_STATE_FLAG = "__callRouteGuardRedirected";

function restoreOrEndCall({ callId, location, navigate, source }) {
  const uid = auth.currentUser?.uid || "";
  if (isValidRouteCallId(callId)) {
    void (async () => {
      try {
        const functions = getFunctions(undefined, "us-central1");
        const endCall = httpsCallable(functions, "endCall");
        await endCall({ callId, reason: `android_web_fallback_${source}` });
      } catch (_e) {
        // Best-effort only - the user must never get stuck here because the
        // callable failed. Returning them safely still proceeds below.
      }
    })();
  }
  const { context } = resolveReturnContext({
    uid,
    callId,
    stateReturnContext: location.state?.returnContext,
    legacyReturnTo: location.state?.returnTo,
  });
  const targetUrl = context?.url || CALL_RETURN_FALLBACK_URL;
  markNativeCallReturnStarted();
  navigate(targetUrl, {
    replace: true,
    state: {
      callReturnScroll: {
        x: context?.scrollX || 0,
        y: context?.scrollY || 0,
      },
      callReturnMessage: mapCallReturnToastMessage(source, "android_web_fallback_blocked"),
    },
  });
  removeReturnContext(uid, callId);
}

/**
 * Explicit blocking UI shown on Android whenever the native call room could
 * not be reached (invalid callId, or a redirect already failed once). Never
 * renders CallRoomPage - Retry re-attempts the native route; End Call/Return
 * safely ends the call server-side (best effort) and restores returnContext.
 */
function AndroidCallBlockedScreen({ callId, canRetry }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleRetryNative = () => {
    logAndroidNativeRedirectFailed({ callId, reason: "manual_retry" });
    navigate(`/call-native/${callId}`, {
      replace: true,
      state: { ...location.state, [REDIRECT_STATE_FLAG]: true },
    });
  };

  const handleEndOrReturn = () => {
    restoreOrEndCall({ callId, location, navigate, source: "blocked_screen" });
  };

  return (
    <div className="native-call-room-fallback">
      <div className="native-call-room-fallback-box" dir="rtl">
        <div className="native-call-room-fallback-status">
          تعذر فتح المكالمة. لا يمكن عرض المكالمة عبر المتصفح على هذا الجهاز.
        </div>
        <div className="native-call-room-fallback-actions">
          {canRetry && (
            <button
              type="button"
              className="native-call-room-fallback-retry-btn"
              onClick={handleRetryNative}
            >
              إعادة المحاولة
            </button>
          )}
          <button
            type="button"
            className="native-call-room-fallback-end-btn"
            onClick={handleEndOrReturn}
          >
            إنهاء المكالمة / العودة
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * LIVORA PHASE 1A — guards the /call/:callId (Web) route.
 *
 * Android must NEVER mount CallRoomPage, on any code path, including the
 * very first render frame. The platform check happens synchronously in the
 * render body (not inside a useEffect) and `children` is only ever returned
 * from the non-Android branch below - CallRoomPage is never part of the
 * rendered tree on Android, so it is never instantiated and never mounts,
 * not even for one frame.
 */
export default function AndroidWebCallRoomGuard({ children }) {
  const { callId } = useParams();
  const location = useLocation();

  if (!isAndroidNativeApp()) {
    return children;
  }

  logAndroidWebRouteBlocked({ callId, source: "web_route_mount" });

  const validCallId = isValidRouteCallId(callId);
  const alreadyRedirected = !!location.state?.[REDIRECT_STATE_FLAG];

  if (validCallId && !alreadyRedirected) {
    return (
      <Navigate
        to={`/call-native/${callId}`}
        replace
        state={{ ...location.state, [REDIRECT_STATE_FLAG]: true }}
      />
    );
  }

  const reason = !validCallId ? "invalid_call_id" : "redirect_loop_detected";
  logAndroidNativeRedirectFailed({ callId, reason });
  logAndroidWebMountPrevented({ callId, reason });

  return (
    <AndroidCallBlockedScreen callId={validCallId ? callId : ""} canRetry={validCallId} />
  );
}

/**
 * Opposite direction: a real browser directly opening /call-native/:callId
 * must never attempt to run the Android/Capacitor plugin path. Same
 * synchronous-render shape as the guard above - NativeCallRoomPage is only
 * ever returned from the Android branch.
 */
export function NativeRouteBrowserGuard({ children }) {
  const { callId } = useParams();
  const location = useLocation();

  if (isAndroidNativeApp()) {
    return children;
  }

  logBrowserNativeRouteBlocked({ callId, source: "native_route_mount" });

  if (!isValidRouteCallId(callId)) {
    // Nothing safe to redirect to either way - send to the same home
    // fallback resolveCallRoomRoute() itself would have used.
    return <Navigate to="/home" replace state={location.state} />;
  }

  return <Navigate to={`/call/${callId}`} replace state={location.state} />;
}
